/**
 * Cookie banner A/B test (22 Oct 2026). UI/UX approved by Chris.
 *
 * A is today's OneTrust banner. B is the same banner, same words, links and
 * buttons, restyled as a full-width bar at 25% of the screen height
 * (src/styles.css, html[data-rh-banner="b"]). Only CSS differs, so accept,
 * decline, Google consent mode and the Zaraz bridge behave the same in both.
 *
 * On or off: the Worker writes the test mode onto <html data-rh-ab> after
 * the edge cache (banner-ab.server.ts). BANNER_AB is "a" in wrangler.jsonc:
 * the test is off, everyone sees A and nothing is counted. "split" starts it.
 *
 * Split: no cookie. The inline <head> script below picks a or b at random,
 * 50/50, and keeps it in sessionStorage for the browser tab, then sets
 * <html data-rh-banner> before OneTrust draws the banner, so it never changes
 * shape on screen. A new tab or a later visit can get the other variant.
 *
 * sessionStorage holds no identifier, in two keys:
 *   rh_bnr       variant . counted flags      for example "b.sv"
 *                variant a | b; flags s banner shown, o banner outcome,
 *                v quote step 1 viewed, q quote step 1 sent, d declined.
 *                Each count is sent once per tab.
 *   rh_bnr_paid  "1" when the landing URL had a click id (gclid, gbraid,
 *                wbraid, gad_source, msclkid) or a paid utm_medium, else "0".
 *                The click id itself is never kept.
 *
 * Counts go to /api/public/banner-ab and are stored per day only
 * (d1/migrations/0005_banner_ab_counts.sql): no IP, user agent or id.
 * Nothing is sent while OneTrust has analytics or marketing turned off. A
 * visitor who turns them off is counted once, as one "decline" per variant
 * and day, and nothing is sent from that tab after that. Their leads are
 * counted on the server from the lead itself (countBannerLead).
 */
export const BANNER_STORAGE_KEY = "rh_bnr";
export const BANNER_PAID_KEY = "rh_bnr_paid";

export type BannerVariant = "a" | "b";
export const BANNER_EVENTS = ["shown", "accept", "decline", "close", "step1_view", "step1_submit"] as const;
export type BannerEvent = (typeof BANNER_EVENTS)[number];
/** y: landed from a paid click; n: did not. */
export const BANNER_PAID = ["y", "n"] as const;
export const BANNER_CONSENT = ["granted", "denied", "none"] as const;

/** YYYY-MM-DD in US Eastern time, the day the counts are stored under. */
export function easternDay(at: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(at);
}

/**
 * The lead's exp_banner value, "variant.paid.seen.accepted" (for example
 * "b.y.1.1"), or null when it is not one of ours. accepted is 1 when the tab
 * closed the banner with consent (Accept, or X / a click / the next page on
 * the notice rule), 0 when it did not, and null for leads sent before the
 * field existed ("variant.paid.seen").
 */
export function parseLeadBanner(
  raw: unknown,
): { v: BannerVariant; p: string; seen: 0 | 1; accepted: 0 | 1 | null } | null {
  const m = /^([ab])\.([yn])\.([01])(?:\.([01]))?$/.exec(typeof raw === "string" ? raw : "");
  if (!m) return null;
  return { v: m[1] as BannerVariant, p: m[2], seen: m[3] === "1" ? 1 : 0, accepted: m[4] === undefined ? null : m[4] === "1" ? 1 : 0 };
}

/**
 * Runs in <head>, before OneTrust and the consent bridge. Sets
 * <html data-rh-banner> and sends each count once per tab:
 *   shown        the OneTrust banner was on screen
 *   accept       banner closed with analytics and marketing allowed
 *   close        banner closed with X (OneTrust interaction type 3). On an
 *                opt-in rule X gives no consent, so it is the last thing sent.
 *   decline      banner closed with analytics or marketing turned off. The
 *                last thing sent from that tab.
 *   step1_view / step1_submit   window.__rhBannerAb.count(), from the quote form
 * It reads OneTrust's cookies and never writes them, and never changes consent.
 */
export const BANNER_AB_INLINE = `(function () {
  if (window.__rhBannerAb) return;
  var KEY = "${BANNER_STORAGE_KEY}";
  var PAID_KEY = "${BANNER_PAID_KEY}";
  var mode = document.documentElement.getAttribute("data-rh-ab") || "off";
  window.__rhBannerAb = { variant: null, count: function () {}, lead: function () { return null; } };
  // OneTrust's notice rule (US National Audience): a page click or the next
  // page accepts, and X accepts. Same test as the consent bridge.
  function noticeRule() {
    try {
      var d = window.OneTrust && typeof OneTrust.GetDomainData === "function" ? OneTrust.GetDomainData() : null;
      if (!d) return null;
      var model = d.ConsentModel && d.ConsentModel.Name;
      return model === "opt-out" && (d.OnClickAcceptAllCookies === true || d.NextPageAcceptAllCookies === true);
    } catch (e) { return null; }
  }
  // Variant B. Notice rule: an Accept button that clicks OneTrust's own X
  // handler, so the result is exactly what X does there today; the X is then
  // hidden (styles.css, data-rh-b="notice"). Opt-in rules keep OneTrust's own
  // buttons (Accept Cookies shown as "Accept") and the X. Nothing here
  // changes consent.
  // Variant B on phones: the legal text shows 2 lines until the visitor taps
  // it or "More" (styles.css clamps it; the whole text stays in the page for
  // screen readers, word for word). The Privacy Policy link moves, as the
  // same element, onto the line with "More", so it is always reachable.
  // Expanding is not a consent action: the click stops here, so OneTrust's
  // "a click accepts" rule never sees it, and nothing is counted.
  function clampText(b) {
    var t = b.querySelector("#onetrust-policy-text");
    if (!t || t.querySelector("#rh-policy-body")) return;
    var link = t.querySelector("a");
    var body = document.createElement("span");
    body.id = "rh-policy-body";
    while (t.firstChild) {
      if (t.firstChild === link) t.removeChild(link);
      else body.appendChild(t.firstChild);
    }
    var row = document.createElement("span");
    row.id = "rh-policy-row";
    var more = document.createElement("button");
    more.type = "button";
    more.id = "rh-policy-more";
    more.setAttribute("aria-controls", "rh-policy-body");
    more.setAttribute("aria-expanded", "false");
    more.textContent = "More";
    function toggle(e) {
      e.stopPropagation();
      var open = t.getAttribute("data-rh-open") !== "1";
      t.setAttribute("data-rh-open", open ? "1" : "0");
      more.setAttribute("aria-expanded", open ? "true" : "false");
      more.textContent = open ? "Less" : "More";
    }
    more.addEventListener("click", toggle);
    body.addEventListener("click", function (e) { if (t.getAttribute("data-rh-open") !== "1") toggle(e); else e.stopPropagation(); });
    row.appendChild(more);
    if (link) row.appendChild(link);
    t.appendChild(body);
    t.appendChild(document.createTextNode(" "));
    t.appendChild(row);
  }
  function noticeAccept() {
    var b = document.getElementById("onetrust-banner-sdk");
    if (!b || b.getAttribute("data-rh-b")) return !!b;
    var notice = noticeRule();
    if (notice === null) return false;
    clampText(b);
    var close = b.querySelector(".onetrust-close-btn-handler");
    var group = b.querySelector("#onetrust-button-group");
    var own = b.querySelector("#onetrust-accept-btn-handler");
    if (!notice || !close || !group || own) {
      // Opt-in rules: OneTrust's own Accept button, labelled "Accept"; its
      // handler is unchanged.
      if (own) own.textContent = "Accept";
      b.setAttribute("data-rh-b", "choice");
      return true;
    }
    var accept = document.createElement("button");
    accept.type = "button";
    accept.id = "rh-notice-accept";
    accept.textContent = "Accept";
    accept.addEventListener("click", function () { close.click(); });
    // [Privacy Preferences] [Accept]
    group.appendChild(accept);
    b.setAttribute("data-rh-b", "notice");
    return true;
  }
  function watchBannerB() {
    if (noticeAccept()) return;
    var mo = new MutationObserver(function () { if (noticeAccept()) mo.disconnect(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 30000);
  }

  // a or off: the test is off, everyone sees A. b: everyone sees B (after
  // the test, if B wins). Neither counts anything.
  if (mode === "b") {
    document.documentElement.setAttribute("data-rh-banner", "b");
    watchBannerB();
  }
  if (mode !== "split" && mode !== "force-a" && mode !== "force-b" && mode !== "keep") return;
  function readCookie(name) {
    var parts = document.cookie.split("; ");
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(name + "=") === 0) return parts[i].slice(name.length + 1);
    }
    return null;
  }
  function read() {
    var raw = null, paid = null;
    try { raw = window.sessionStorage.getItem(KEY); paid = window.sessionStorage.getItem(PAID_KEY); } catch (e) {}
    var m = /^([ab])\\.([sovqd]*)$/.exec(raw || "");
    return m ? { v: m[1], p: paid === "1" ? "y" : paid === "0" ? "n" : "", f: m[2] || "" } : null;
  }
  function write(c) {
    try {
      window.sessionStorage.setItem(KEY, c.v + "." + c.f);
      window.sessionStorage.setItem(PAID_KEY, c.p === "y" ? "1" : "0");
      return true;
    } catch (e) { return false; }
  }
  var c = read();
  // keep (previews only): stay on a variant forced earlier in this tab, else A.
  if (mode === "keep" && !c) return;
  // force-a / force-b: a preview link with ?banner=. split: 50/50, kept
  // for this tab.
  var forced = /^force-([ab])$/.exec(mode);
  var fixed = forced ? forced[1] : null;
  if (fixed && (!c || c.v !== fixed)) c = { v: fixed, p: c ? c.p : "", f: "" };
  if (!c) {
    var r = new Uint8Array(1);
    try { window.crypto.getRandomValues(r); } catch (e) { r[0] = Math.random() * 256; }
    c = { v: r[0] < 128 ? "a" : "b", p: "", f: "" };
  }
  // Paid flag from the landing URL: whether a click id is present, never its value.
  if (!c.p) {
    var q = new URLSearchParams(location.search);
    var medium = (q.get("utm_medium") || "").toLowerCase();
    c.p = q.get("gclid") || q.get("gbraid") || q.get("wbraid") || q.get("gad_source") || q.get("msclkid") ||
      /^(cpc|ppc|paid|paidsearch|paid_search|paid-search|sem|display|paid_social|paidsocial)$/.test(medium) ? "y" : "n";
  }
  // Without sessionStorage the variant could change on every page: show A
  // and count nothing.
  if (!write(c)) return;
  document.documentElement.setAttribute("data-rh-banner", c.v);
  if (c.v === "b") watchBannerB();
  window.__rhBannerAb.variant = c.v;
  window.__rhBannerAb.lead = function () {
    // Catch up first, so a banner closed by this very page view counts.
    try { check(); } catch (e) {}
    var now = read() || c;
    // accepted: closed with consent ("o" without "d"; an opt-in X or a
    // decline sets both).
    var accepted = now.f.indexOf("o") >= 0 && now.f.indexOf("d") < 0;
    return now.v + "." + (now.p || "n") + "." + (now.f.indexOf("s") >= 0 ? "1" : "0") + "." + (accepted ? "1" : "0");
  };

  // OneTrust's OptanonConsent groups: C0002 analytics, C0004 marketing.
  function consentState() {
    var raw = readCookie("OptanonConsent");
    if (!raw) return "none";
    var groups = (/(?:^|&)groups=([^&]*)/.exec(raw) || [])[1];
    if (!groups) return "none";
    groups = decodeURIComponent(groups);
    if (/(?:^|,)C000[24]:0/.test(groups)) return "denied";
    return /(?:^|,)C0002:1/.test(groups) && /(?:^|,)C0004:1/.test(groups) ? "granted" : "none";
  }
  function intType() {
    var raw = readCookie("OptanonConsent") || "";
    return (/(?:^|&)intType=([^&]*)/.exec(raw) || [])[1] || "";
  }
  function closed() {
    return readCookie("OptanonAlertBoxClosed") !== null;
  }
  function bannerOnScreen() {
    var b = document.getElementById("onetrust-banner-sdk");
    if (!b || closed()) return false;
    var s = window.getComputedStyle(b);
    return s.display !== "none" && s.visibility !== "hidden" && Number(s.opacity) > 0 && b.offsetHeight > 0;
  }
  function send(event, consent) {
    var body = JSON.stringify({ v: c.v, e: event, p: c.p, c: consent, x: c.f.indexOf("s") >= 0 ? 1 : 0 });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon("/api/public/banner-ab", new Blob([body], { type: "application/json" }))) return;
    } catch (e) {}
    try {
      fetch("/api/public/banner-ab", { method: "POST", body: body, keepalive: true, headers: { "content-type": "application/json" } });
    } catch (e) {}
  }
  // After the banner closes, the consent bridge allows tracking only when
  // the groups are on and, outside the notice rule, the close was a choice
  // (not X). Same here: otherwise nothing more is sent.
  function allowedAfterClose() {
    if (consentState() === "denied") return false;
    var notice = noticeRule();
    if (notice === null) return null;
    return notice || intType() !== "3";
  }
  function once(flag, event) {
    var now = read();
    if (now) c = now;
    // d: this tab declined. Nothing more is sent from it.
    if (c.f.indexOf(flag) >= 0 || c.f.indexOf("d") >= 0) return;
    var consent = consentState();
    if (consent === "denied") return;
    if (closed()) {
      var allowed = allowedAfterClose();
      // OneTrust not loaded yet on this page: ask again shortly.
      if (allowed === null) return "later";
      if (!allowed) return;
    }
    c.f += flag;
    write(c);
    send(event, consent);
  }
  function check() {
    var now = read();
    if (now) c = now;
    if (c.f.indexOf("d") >= 0) return;
    if (c.f.indexOf("s") < 0 && bannerOnScreen()) once("s", "shown");
    if (c.f.indexOf("s") >= 0 && c.f.indexOf("o") < 0 && closed()) {
      var allowed = allowedAfterClose();
      if (allowed === null) return;
      if (!allowed) {
        // Declined, or X on an opt-in rule (no consent): one aggregate count
        // (variant, paid flag, day), then nothing more from this tab.
        c.f += "od";
        write(c);
        send(consentState() === "denied" ? "decline" : "close", "denied");
      } else {
        once("o", intType() === "3" ? "close" : "accept");
      }
    }
  }
  function countStep(event, tries) {
    try {
      check();
      var r = event === "step1_view" ? once("v", event) : event === "step1_submit" ? once("q", event) : null;
      if (r === "later" && tries < 20) setTimeout(function () { countStep(event, tries + 1); }, 500);
    } catch (e) {}
  }
  window.__rhBannerAb.count = function (event) { countStep(event, 0); };
  window.addEventListener("OneTrustGroupsUpdated", function () { try { check(); } catch (e) {} });
  document.addEventListener("visibilitychange", function () { try { check(); } catch (e) {} });
  window.addEventListener("pagehide", function () { try { check(); } catch (e) {} });
  var ticks = 0;
  var timer = setInterval(function () {
    ticks++;
    try { check(); } catch (e) {}
    // Look every second for 30 s, then rely on the events above.
    if (ticks >= 30 || c.f.indexOf("o") >= 0) clearInterval(timer);
  }, 1000);
})();`;

type BannerGlobal = { __rhBannerAb?: { count?: (e: string) => void; lead?: () => string | null } };

/** Count a quote step for the banner test. Safe to call anywhere. */
export function countBannerStep(event: "step1_view" | "step1_submit"): void {
  if (typeof window === "undefined") return;
  try {
    (window as unknown as BannerGlobal).__rhBannerAb?.count?.(event);
  } catch {
    /* counting must never affect the form */
  }
}

/**
 * The lead's tracking JSON with exp_banner ("variant.paid.seen.accepted") added while
 * the test runs. It goes to the Sheet's tracking cell, and the server counts
 * the lead per variant from it. No Salesforce field carries it (proposed:
 * Banner_Variant__c).
 */
export function withBannerVariant<T extends Record<string, string>>(tracking: T): T & { exp_banner?: string } {
  if (typeof window === "undefined") return tracking;
  try {
    const v = (window as unknown as BannerGlobal).__rhBannerAb?.lead?.();
    return v ? { ...tracking, exp_banner: v } : tracking;
  } catch {
    return tracking;
  }
}
